import {
  DH_DEFAULT_VOICE_LANGUAGE,
  DH_DEFAULT_VOICE_STYLE,
  isKnownVoiceDialect,
  isKnownVoiceLanguage,
  isKnownVoiceStyle,
} from "@/entities/dh-profile/model/voiceV2Options";

/**
 * 换音色重生成的「没换音色」闸(修复波 0926 第十一批 B2 #18)。
 *
 * ★K4 实测:弹窗初态只渲染默认音色一项,不改选就能直接确认 —— 没换音色也扣了 840,出了一条一模一样的片。
 * ★判据:选中的音色与**原单**音色相同 → 不许确认(按钮禁用 + 提示「和当前音色一样,换一个再出片」)。
 *   原单音色取任务 settings(voiceSource + presetVoice / voiceRef);老单没这两个字段 → 判不出 → 不拦
 *   (拦一个判不出的东西等于把功能锁死)。
 */
export interface RevoiceTuning {
  language: string;
  dialect: string | null;
  style: string;
}

export interface RevoiceSelection {
  voiceKind: "preset" | "profile";
  presetVoice: string;
  profileId: string;
  /**
   * 声纹路的三件套(1006):同一条声纹但改了语言/方言/演绎 = 改了,放行。
   * 不给(或旧版声纹三件套不生效时传 null)→ 只按声纹 id 判,与原口径一致。
   */
  tuning?: RevoiceTuning | null;
}

export interface RevoiceCurrentVoice {
  voiceSource?: string | null;
  presetVoice?: string | null;
  voiceRef?: string | null;
  voiceLanguage?: string | null;
  voiceDialect?: string | null;
  voiceStyle?: string | null;
}

/**
 * 原任务三件套的继承值(词表验型,认不出的落默认)—— 换音色弹层打开时的初值,
 * 也是「没改」闸比对的基准(两处同一份,不然初态就会被判成「改过」)。
 */
export function inheritedTuningOf(st: RevoiceCurrentVoice | null | undefined): RevoiceTuning {
  const lang = st?.voiceLanguage;
  const language = isKnownVoiceLanguage(lang) ? lang : DH_DEFAULT_VOICE_LANGUAGE;
  const dialect = st?.voiceDialect;
  const style = st?.voiceStyle;
  return {
    language,
    dialect: language === "zh" && isKnownVoiceDialect(dialect) ? dialect : null,
    style: isKnownVoiceStyle(style) ? style : DH_DEFAULT_VOICE_STYLE,
  };
}

/** 原单用的预置音色 id(只在 voiceSource=preset 且有值时给;别的情况 null)。 */
export function currentPresetVoiceOf(st: RevoiceCurrentVoice | null | undefined): string | null {
  if (!st || st.voiceSource !== "preset") return null;
  return st.presetVoice?.trim() ? st.presetVoice.trim() : null;
}

export function revoiceSameAsCurrent(
  sel: RevoiceSelection,
  st: RevoiceCurrentVoice | null | undefined
): boolean {
  if (!st) return false;
  if (sel.voiceKind === "preset") {
    const cur = currentPresetVoiceOf(st);
    return cur != null && cur === sel.presetVoice;
  }
  if (st.voiceSource !== "profile" || !st.voiceRef) return false;
  if (sel.profileId === "" || String(st.voiceRef) !== sel.profileId) return false;
  // 同一条声纹:三件套也没动才算「没改」(1006 弹层默认继承原声纹后,只改方言/演绎是正当诉求)。
  if (!sel.tuning) return true;
  const cur = inheritedTuningOf(st);
  const selDialect = sel.tuning.language === "zh" ? sel.tuning.dialect : null;
  return (
    cur.language === sel.tuning.language &&
    cur.dialect === selDialect &&
    cur.style === sel.tuning.style
  );
}

export const REVOICE_SAME_HINT = "和当前音色一样,换一个再出片";
